import React, { useState, useEffect, useRef } from "react";
import { ref, update, set, onValue } from 'firebase/database';
import { database, firestore } from '../../firebaseConfig';
import { doc, updateDoc, getDoc, onSnapshot } from 'firebase/firestore';
import EmailAiWebPreview from '../EmailAiWebPreview';
import { addFooterToEmail, convertToHtml } from '../EmailFooterGenerator';
import EmailAiPopupTopics from '../EmailAiPopupTopics';
import { CLOUD_FUNCTION_URLS } from '../../utils/cloudFunctions';

// Helper-Funktion für URL-Formatierung
const formatURL = (url, prefix) => {
  if (!url) return '';
  
  // Wenn URL bereits http:// oder https:// enthält
  if (url.match(/^https?:\/\//i)) {
    return url;
  }
  
  // Entferne @ von Instagram-Handles
  if (prefix === 'https://instagram.com/') {
    url = url.replace('@', '');
  }
  
  return `${prefix}${url}`;
};

const NormalModeEmail = ({ 
  onClose, 
  rowData, 
  userId, 
  isReply, 
  source, 
  onEmailResult,
  userSettings,
  aiSettings,
  userTopics,
  isTopicsLoaded,
  senderEmails,
  topicTypeMapping
}) => {
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("Professional & Supportive");
  const [contextInfo, setContextInfo] = useState("");
  const [subjectLine, setSubjectLine] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [tempEmails, setTempEmails] = useState([]);
  const [isStatusSet, setIsStatusSet] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [ccEmails, setCcEmails] = useState([]);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");
  const [showBlockingOverlay, setShowBlockingOverlay] = useState(false);
  
  // Ref für automatische Generierung
  const hasAutoGenerated = useRef(false);
  // Ref für das Betreff-Input-Element
  const subjectInputRef = useRef(null);

  // Emailadressen aus rowData extrahieren
  const emailAddresses = React.useMemo(() => {
    if (!rowData.lead_email) return [];
    return rowData.lead_email.split(',').map(email => email.trim()).filter(Boolean);
  }, [rowData.lead_email]);

  // Erste Email-Adresse auswählen, wenn verfügbar
  useEffect(() => {
    if (emailAddresses.length > 0) {
      setSelectedEmail(emailAddresses[0]);
    }
  }, [emailAddresses]);

  // Kontextinfo aus rowData setzen
  useEffect(() => {
    if (rowData) {
      const contextText = `
Business Name: ${rowData.business_name || ''}
City: ${rowData.lead_city || ''}
Description: ${rowData.lead_description || ''}
Type: ${rowData.lead_type || ''}
      `.trim();
      setContextInfo(contextText);
    }
  }, [rowData]);

  // Nächsten Follow-Up-Schritt ermitteln
  const getNextFollowUpStep = (currentStatus) => {
    switch (currentStatus) {
      case 'Introduced':
        return "Follow Up";
      case 'First Follow-up':
        return "Second Follow Up";
      case 'Second Follow-up':
        return "Third Follow Up";
      case 'Third Follow-up':
        return null; // Alle Follow-ups wurden bereits gesendet
      default:
        return "Introduce Yourself";
    }
  };

  // Topic basierend auf Kontaktstatus setzen
  useEffect(() => {
    // Prüfe zuerst, ob ein selectedTopic aus den rowData vorhanden ist (von AI Tags)
    if (rowData.selectedTopic) {
      setSelectedTopic(rowData.selectedTopic);
      setSelectedStyle("Professional & Supportive");
      setIsStatusSet(true);
      return; // Früher Return, um die Standard-Logik zu überspringen
    }
    
    // Robuste Prüfung für lead_contacted - berücksichtige verschiedene mögliche Feldnamen und undefined/null Werte
    const contactedStatus = rowData.lead_contacted || rowData.lead_status || null;
    const hasReplied = rowData.lead_reply === true || rowData.lead_has_replied === true;
    
    // Wenn der Lead noch nicht kontaktiert wurde (null, undefined, oder "Not Contacted")
    if (!contactedStatus || contactedStatus === "Not Contacted" || contactedStatus === "" || contactedStatus === null || contactedStatus === undefined) {
      setSelectedTopic("Introduce Yourself");
      setSelectedStyle("Professional & Supportive");
      setIsStatusSet(false);
    } else {
      const nextFollowUp = getNextFollowUpStep(contactedStatus);
      if (nextFollowUp) {
        setSelectedTopic(nextFollowUp);
        setSelectedStyle("Professional & Supportive");
      } else {
        // Fallback, wenn kein Follow-up bestimmt werden kann
        setSelectedTopic("Introduce Yourself");
        setSelectedStyle("Professional & Supportive");
      }
      setIsStatusSet(true);
    }
  }, [rowData.lead_contacted, rowData.lead_status, rowData.lead_reply, rowData.lead_has_replied, rowData.selectedTopic]);

  // Topics für Antworten
  const getAvailableTopics = () => {
    const nextFollowUp = getNextFollowUpStep(rowData.lead_contacted);
    if (!nextFollowUp) {
      return ["Introduce Yourself", "Follow Up", "Second Follow Up", "Third Follow Up"];
    }
    return [nextFollowUp];
  };

  // Hilfsfunktion für Standard-Topic-Anweisungen
  const getDefaultTopicNote = (topicType) => {
    const defaultNotes = {
      'intro': "Bitte stelle unser Unternehmen vor, teile einige spannende Details darüber, wie wir helfen können, und füge einen Call-to-Action hinzu.",
      'follow_up_1': "Erkundige dich höflich, ob der Empfänger die vorherige E-Mail erhalten hat. Halte es kurz und freundlich.",
      'follow_up_2': "Dies ist das zweite Follow-up. Frage höflich nach, ob der Empfänger noch interessiert ist, ohne aufdringlich zu sein.",
      'follow_up_3': "Dies ist das letzte Follow-up. Mache einen letzten sanften Versuch, den Empfänger zu erreichen.",
      'custom_reply': "Bitte verfasse eine durchdachte Antwort auf die Nachricht des Empfängers."
    };
    
    return defaultNotes[topicType] || "Bitte schreibe eine professionelle E-Mail an den Empfänger.";
  };

  // Topic-spezifische Notiz holen
  const getTopicSpecificNote = () => {
    if (!selectedTopic) return "";
    
    // Wenn das Topic von einem AI Tag stammt, suche direkt in userTopics
    if (rowData.selectedTopic && rowData.selectedTopic === selectedTopic) {
      // Suche nach dem Topic in userTopics (sowohl selectedTopics als auch customTopics)
      const matchingTopic = Object.values(userTopics).find(topicData => {
        // Prüfe sowohl topic als auch type Felder
        return topicData.topic === selectedTopic || topicData.type === selectedTopic;
      });
      
      if (matchingTopic) {
        if (matchingTopic.topicSpecificNote) {
          return matchingTopic.topicSpecificNote;
        } else {
          return "";
        }
      } else {
        return "";
      }
    }
    
    // Fallback auf die normale Logik
    const topicType = topicTypeMapping[selectedTopic];
    if (!topicType) return "";
    
    const foundTopic = Object.values(userTopics).find(topic => topic.type === topicType);
    
    if (foundTopic && foundTopic.topicSpecificNote) {
      return foundTopic.topicSpecificNote;
    }
    
    return getDefaultTopicNote(topicType);
  };

  // Betreffzeile für das Topic holen
  const getSubjectLineForTopic = async (topic) => {
    if (!topic || !rowData) return "";
    
    // Wenn das Topic von einem AI Tag stammt (rowData.selectedTopic ist gesetzt), 
    // suche das entsprechende Topic in den userTopics
    if (rowData.selectedTopic && rowData.selectedTopic === topic) {
      // Suche nach dem Topic in userTopics (sowohl selectedTopics als auch customTopics)
      const matchingTopic = Object.values(userTopics).find(topicData => {
        // Prüfe sowohl topic als auch type Felder
        return topicData.topic === topic || topicData.type === topic;
      });
      
      if (matchingTopic) {
        if (matchingTopic.subjectLine) {
          const aiSubjectLine = replacePlaceholders(matchingTopic.subjectLine);
          return aiSubjectLine;
        } else {
          return "";
        }
      } else {
        return "";
      }
    }
    
    // Fallback auf die normale Logik
    const topicType = topicTypeMapping[topic];
    if (!topicType) return "";
    
    const matchingTopic = Object.values(userTopics).find(t => t.type === topicType);
    
    if (matchingTopic && matchingTopic.subjectLine) {
      return replacePlaceholders(matchingTopic.subjectLine);
    }
    
    if (userSettings.subject_line) {
      return replacePlaceholders(userSettings.subject_line);
    }
    
    const defaultSubjects = {
      "Introduce Yourself": `Boost for ${rowData.business_name || 'Ihr Unternehmen'}`,
      "Follow Up": `Nachfrage - Partnerschaft mit ${rowData.business_name || 'Ihrem Unternehmen'}`,
      "Second Follow Up": `Kurzes Update für ${rowData.business_name || 'Ihr Unternehmen'}`,
      "Third Follow Up": `Nachfrage für ${rowData.business_name || 'Ihr Unternehmen'}`
    };
    
    return defaultSubjects[topic] || `Boost for ${rowData.business_name || 'Ihr Unternehmen'}`;
  };

  // Platzhalter ersetzen
  const replacePlaceholders = (text) => {
    if (!text) return '';
    
    return text
      .replace(/{{\s*business_name\s*}}/g, rowData.business_name || '')
      .replace(/{{\s*lead_name\s*}}/g, rowData.lead_name || '')
      .replace(/{{\s*lead_firstname\s*}}/g, rowData.lead_firstname || '')
      .replace(/{{\s*lead_lastname\s*}}/g, rowData.lead_lastname || '')
      .replace(/{{\s*user_name\s*}}/g, userSettings.user_name || '')
      .replace(/{{\s*company_name\s*}}/g, userSettings.company_name || 'BonusClub');
  };

  // Betreffzeile aktualisieren, wenn sich Topic ändert
  useEffect(() => {
    if (!selectedTopic || !rowData || Object.keys(userTopics).length === 0) return;
    
    const updateSubjectLine = async () => {
      try {
        const newSubject = await getSubjectLineForTopic(selectedTopic);
        setSubjectLine(newSubject);
      } catch (error) {
        // Fallback auf eine einfache Betreffzeile
        setSubjectLine(`BonusClub for ${rowData.business_name || 'Ihr Unternehmen'}`);
      }
    };
    
    const timeoutId = setTimeout(() => {
      updateSubjectLine();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [selectedTopic, userTopics, rowData]);

  // Automatische Generierung beim ersten Laden
  useEffect(() => {
    if (
      !hasAutoGenerated.current && 
      isTopicsLoaded && 
      selectedTopic && 
      selectedStyle && 
      !isGenerating
    ) {
      hasAutoGenerated.current = true;
      setTimeout(() => {
        handleGenerate();
      }, 500);
    }
  }, [isTopicsLoaded, selectedTopic, selectedStyle, isGenerating]);

  // E-Mail generieren
  const handleGenerate = async () => {
    if (isGenerating) return;
    
    const topic = selectedTopic;
    const style = selectedStyle;
    const prompt = contextInfo;
    
    setIsGenerating(true);
    setBodyContent("");
    setSendSuccess(false);
    
    try {
      // Für normale E-Mails: Hole die spezifische Notiz für den ausgewählten Topic
      // Berücksichtige sowohl benutzerdefinierte als auch Standard-Topics
      let topicSpecificNote = "";
      
      const customTopic = Object.entries(userTopics).find(([_, data]) => data.topic === topic);
      if (customTopic && customTopic[1].topicSpecificNote) {
        topicSpecificNote = customTopic[1].topicSpecificNote;
      } else {
        // Für andere Typen: Suche nach dem Topic mit dem entsprechenden Typ
        const topicType = topicTypeMapping[topic];
        if (topicType) {
          const foundTopic = Object.values(userTopics).find(topic => topic.type === topicType);
          if (foundTopic && foundTopic.topicSpecificNote) {
            topicSpecificNote = foundTopic.topicSpecificNote;
          } else {
            // Fallback auf Standard-Anweisungen
            topicSpecificNote = getDefaultTopicNote(topicType);
          }
        }
      }
      
      // KI-Modellparameter
      const model = aiSettings.model || 'gpt-4o-mini';
      const temperature = parseFloat(aiSettings.temperature) || 0.9;
      const maxTokens = parseInt(aiSettings.max_tokens) || 250;
      const language = aiSettings.language || 'English';
      const promptTemplate = aiSettings.promptTemplate || null;
      const strictInstructions = aiSettings.strictInstructions || null;

      const requestData = {
        topic,
        style,
        context: prompt,
        businessName: rowData.business_name || '',
        website: rowData.lead_website || '',
        senderName: userSettings.user_name || '',
        senderEmail: userSettings.user_email || '',
        firstName: rowData.lead_firstname || '',
        userId: userId,
        topicSpecificNote: topicSpecificNote,
        model: model,
        temperature: temperature,
        maxTokens: maxTokens,
        language: language,
        includeAllDetails: true,
        isReply: isReply,
        promptTemplate: promptTemplate,
        strictInstructions: strictInstructions,
        separateSubjectAndBody: true,
        excludeSubjectFromBody: true,
        bodyOnlyGeneration: true,
        preventSubjectInContent: true
      };

      // Füge soziale Profil-URLs hinzu, falls vorhanden
      if (rowData.instagram_url) {
        requestData.instagram = rowData.instagram_url;
      }
      
      if (rowData.facebook_url) {
        requestData.facebook = rowData.facebook_url;
      }
      
      if (rowData.tiktok_url) {
        requestData.tiktok = rowData.tiktok_url;
      }

      const response = await fetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`E-Mail-Generierung fehlgeschlagen: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setBodyContent(data.emailContent);
      
      if ((!subjectLine || subjectLine.trim() === '') && data.subjectLine) {
        setSubjectLine(data.subjectLine);
      }
    } catch (error) {
      console.error('Fehler bei der E-Mail-Generierung:', error);
      alert('Die E-Mail konnte nicht generiert werden. Bitte versuche es erneut.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Kontaktstatus aktualisieren
  const updateContactStatus = async (userId, leadId, newStatus) => {
    if (!userId || !leadId || !newStatus) {
      return false;
    }
    try {
      const timestamp = Date.now();

      // Optimistisches UI-Update
      if (onEmailResult) {
        const statusUpdateInfo = {
          id: leadId,
          field: 'lead_contacted',
          value: newStatus,
          timestamp,
          _optimistic_update: true,
          _prevent_refresh: true,
          _critical_update: true,
        };
        onEmailResult('status-update', statusUpdateInfo);

        onEmailResult('status-update', {
          id: leadId,
          field: 'lead_status',
          value: newStatus,
          timestamp,
          _optimistic_update: true,
        });
      }

      const updateData = {
        lead_contacted: newStatus,
        lead_status: newStatus,
        last_contact_date: new Date().toISOString(),
        updated_at: timestamp,
        _optimistic_update: true,
        _client_update_timestamp: timestamp,
      };

      if (newStatus !== 'Not Contacted') {
        updateData.lead_last_email = {
          timestamp,
          status: newStatus,
        };
      }

      const contactDocRef = doc(firestore, `World/${userId}/db/${leadId}`);
      await updateDoc(contactDocRef, updateData);

      // Warte kurz auf den bestätigten Wert, um eventuelle Optimismus-Mismatches auszubügeln
      waitForServerConfirmation(userId, leadId, 'lead_contacted', confirmed => {
        onEmailResult('status-update', {
          id: leadId,
          field: 'lead_contacted',
          value: confirmed.lead_contacted,
          timestamp: confirmed.updated_at || Date.now()
        });
        onEmailResult('status-update', {
          id: leadId,
          field: 'lead_status',
          value: confirmed.lead_status,
          timestamp: confirmed.updated_at || Date.now()
        });
      });

      return true;
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Kontaktstatus:', error);
      return false;
    }
  };

  // Letzten Absender in der Datenbank speichern
  const saveLastSenderToDatabase = async (senderEmail) => {
    try {
      if (!userId || !senderEmail) return false;
      
      const lastSenderRef = ref(database, `/World/${userId}/settings/lastUsedSender`);
      await set(lastSenderRef, {
        email: senderEmail,
        timestamp: Date.now(),
        updatedAt: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Fehler beim Speichern des letzten Absenders:', error);
      return false;
    }
  };

  // AI Tag basierend auf Topic setzen
  const setAiTagBasedOnTopic = async (topic, contactId) => {
    try {
      // Erweiterte Suche: Prüfe sowohl in selectedTopics als auch in customTopics
      let matchingTopic = null;
      
      // 1. Suche in allen userTopics (sowohl selectedTopics als auch customTopics)
      matchingTopic = Object.values(userTopics).find(topicData => {
        return topicData.topic === topic || topicData.type === topic;
      });
      
      // 2. Falls nicht gefunden, erweiterte Suche in der Struktur
      if (!matchingTopic && userTopics.selectedTopics) {
        matchingTopic = Object.values(userTopics.selectedTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      // 3. Falls immer noch nicht gefunden, suche in customTopics
      if (!matchingTopic && userTopics.customTopics) {
        matchingTopic = Object.values(userTopics.customTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      if (!matchingTopic) {
        return false;
      }
      
      // Prüfe, ob das Topic einen zugeordneten AI Tag hat
      if (!matchingTopic.aiTagId) {
        return false;
      }
      
      // Lade die AI Tag-Einstellungen
      const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${matchingTopic.aiTagId}`);
      const aiTagSnapshot = await new Promise((resolve) => {
        onValue(aiTagRef, resolve, { onlyOnce: true });
      });
      
      if (!aiTagSnapshot.exists()) {
        return false;
      }
      
      const aiTagData = aiTagSnapshot.val();
      
      // Erstelle den neuen Tag für den Kontakt
      const newTag = {
        tag: aiTagData.tag,
        name: aiTagData.name,
        color: aiTagData.color,
        icon: aiTagData.icon,
        appliedAt: new Date().toISOString(),
        appliedBy: 'email_send',
        appliedTopic: topic,
        ...(aiTagData.selectedTopic && { selectedTopic: aiTagData.selectedTopic })
      };
      
      const contactDocRef = doc(firestore, `World/${userId}/db/${contactId}`);

      if (aiTagData.overwriteOtherTags === true) {
        await updateDoc(contactDocRef, { tags: { [matchingTopic.aiTagId]: newTag } });
      } else {
        const contactSnap = await getDoc(contactDocRef);
        const existingTags = contactSnap.exists() ? contactSnap.data().tags || {} : {};
        await updateDoc(contactDocRef, { [`tags.${matchingTopic.aiTagId}`]: newTag, tags: { ...existingTags, [matchingTopic.aiTagId]: newTag } });
      }
      
      return true;
      
    } catch (error) {
      console.error('🏷️ [AI-TAG-SETTER] Error setting AI tag:', error);
      return false;
    }
  };

  // E-Mail senden
  const handleSend = async () => {
    if (!rowData.lead_email) {
      console.error("No email address found for this contact");
      onEmailResult('error', 'No email address found for this contact');
      return;
    }

    const currentId = rowData.id;
    const currentContent = bodyContent;
    const currentSubject = subjectLine;
    const currentSelectedEmail = selectedEmail;
    const currentCcEmails = [...ccEmails];
    const currentSenderEmail = selectedSenderEmail;
    const currentBusinessName = rowData.business_name || rowData.lead_name || 'Unknown Contact';

    setIsSending(true);
    
    // Neuen Kontaktstatus bestimmen
    let newStatus = null;
    if (!isReply) {
      if (selectedTopic === "Introduce Yourself") {
        newStatus = "Introduced";
      } else if (selectedTopic === "Follow Up") {
        newStatus = "First Follow-up";
      } else if (selectedTopic === "Second Follow Up") {
        newStatus = "Second Follow-up";
      } else if (selectedTopic === "Third Follow Up") {
        newStatus = "Third Follow-up";
      }
      
      // Kontaktstatus aktualisieren
      if (newStatus) {
        await updateContactStatus(userId, currentId, newStatus);
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    // Modal sofort schließen (Normal-Modus-Verhalten)
    onClose();
    
    // E-Mail im Hintergrund senden
    (async () => {
      try {
        // Absender-E-Mail finden
        const senderEmailData = senderEmails.find(item => item.email === currentSenderEmail);
        
        if (!senderEmailData) {
          throw new Error("The selected sender email was not found.");
        }
        
        // E-Mail formatieren
        const formattedContent = convertToHtml(currentContent, userSettings);
        let contentWithFooter = addFooterToEmail(formattedContent, userSettings);
        
        // Request-Daten erstellen
        const requestData = {
          recipients: [currentSelectedEmail, ...currentCcEmails],
          cc: currentCcEmails,
          subject: currentSubject || '',
          content: contentWithFooter || '',
          userId: userId,
          businessName: currentBusinessName,
          leadId: currentId,
          emailType: topicTypeMapping[selectedTopic] || "unknown_topic_type",
          senderEmail: senderEmailData.email.trim(),
          senderPassword: senderEmailData.password,
          senderName: senderEmailData.sender_name || userSettings.user_name || 'BonusClub',
          imapHost: senderEmailData.imap_host,
          imapPort: senderEmailData.imap_port,
          smtpHost: senderEmailData.smtp_host,
          smtpPort: senderEmailData.smtp_port,
          encryptionType: senderEmailData.encryption_type,
          smtpEncryption: String(senderEmailData.smtp_encryption),
          emailId: senderEmailData.id
        };
        
        // E-Mail senden
        const response = await fetch(CLOUD_FUNCTION_URLS.sendWorldEmail(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });
        
        const responseText = await response.text();
        
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          console.error('Fehler beim Parsen der JSON-Antwort:', e);
          if (onEmailResult) {
            onEmailResult('error', `Server response is not valid JSON: ${responseText.substring(0, 100)}...`, rowData);
          }
          return;
        }
        
        if (data.success) {
          // Erfolg – zuerst Toast
          if (onEmailResult) {
            onEmailResult('success', `Email to ${currentBusinessName} successfully sent`, rowData, null, currentSelectedEmail);
          }

          // Sofortiges optimistisches Status-Patch (gleichzeitig mit Toast)
          if (newStatus && onEmailResult) {
            const ts = Date.now();
            const patchPayload = (field) => ({
              id: currentId,
              field,
              value: newStatus,
              timestamp: ts,
              _optimistic_update: true,
              _prevent_refresh: true,
              _critical_update: true
            });

            onEmailResult('status-update', patchPayload('lead_contacted'));
            onEmailResult('status-update', patchPayload('lead_status'));
          }

          // Letzten Absender speichern
          await saveLastSenderToDatabase(currentSenderEmail);

          // AI Tag basierend auf Topic setzen
          await setAiTagBasedOnTopic(selectedTopic, currentId);
        } else {
          // Fehler
          let errorMessage = data.error || 'Unknown error sending email';
          
          if (data.error && data.error.includes("Sender address rejected")) {
            errorMessage = `Die E-Mail-Adresse "${senderEmailData.email}" wurde vom Server abgelehnt.`;
          }
          
          if (onEmailResult) {
            onEmailResult('error', errorMessage, rowData);
          }
        }
      } catch (error) {
        console.error("Fehler beim Senden der E-Mail:", error);
        
        if (onEmailResult) {
          onEmailResult('error', error.message || 'Unknown error sending email', rowData);
        }
      }
    })();
  };

  // CC-Status für eine E-Mail umschalten
  const toggleCcEmail = (email) => {
    if (ccEmails.includes(email)) {
      setCcEmails(ccEmails.filter(e => e !== email));
    } else {
      if (email !== selectedEmail) {
        setCcEmails([...ccEmails, email]);
      }
    }
  };

  // Direktes Aktualisieren des Betreffs
  const updateSubjectInputDirectly = (subject) => {
    setSubjectLine(subject);
    
    if (subjectInputRef.current) {
      subjectInputRef.current.value = subject;
      
      const event = new Event('input', { bubbles: true });
      subjectInputRef.current.dispatchEvent(event);
    }
  };

  // Prüfen, ob alle erforderlichen Felder ausgefüllt sind
  const isEmailValid = () => {
    return (
      selectedEmail && 
      selectedEmail.trim() !== '' && 
      selectedSenderEmail && 
      selectedSenderEmail.trim() !== '' && 
      subjectLine && 
      subjectLine.trim() !== '' && 
      bodyContent && 
      bodyContent.trim() !== ''
    );
  };

  // Absender-E-Mail automatisch auswählen
  useEffect(() => {
    if (senderEmails.length > 0) {
      // Setze den letzten verwendeten Absender
      const lastSenderRef = ref(database, `/World/${userId}/settings/lastUsedSender`);
      const unsubscribe = onValue(lastSenderRef, (snapshot) => {
        if (snapshot.exists()) {
          const lastSender = snapshot.val();
          const matchingEmail = senderEmails.find(email => email.email === lastSender.email);
          if (matchingEmail) {
            setSelectedSenderEmail(matchingEmail.email);
            return;
          }
        }
        // Fallback auf die erste E-Mail
        setSelectedSenderEmail(senderEmails[0].email);
      }, { onlyOnce: true });
      
      return () => unsubscribe();
    }
  }, [senderEmails, userId]);

  const waitForServerConfirmation = (userId, contactId, field, apply) => {
    try {
      const unsub = onSnapshot(
        doc(firestore, `World/${userId}/db/${contactId}`),
        snap => {
          if (snap.exists()) {
            const data = snap.data();
            apply(data);
            unsub();
          }
        },
        err => {
          console.error('Snapshot error:', err);
          unsub();
        }
      );
    } catch (e) {
      console.error('waitForServerConfirmation error', e);
    }
  };

  return (
    <div className={`emailAiPopupContainer ${isReply ? 'reply-mode' : ''}`}>
      <div className="emailAiPopupHeader">
        <h2>AI E-Mail Generator</h2>
        <button className="emailAiPopupCloseButton" onClick={onClose}>×</button>
      </div>
      
      <div className="emailAiPopupBusinessHeader">
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
        <img style={{width: '40px', height: '40px'}} src="/img/flieger.webp" alt="Business" className="emailAiPopupBusinessIcon" />
        <div className="emailAiPopupBusinessName">
          <div style={{display: 'flex', textAlign: 'left', justifyContent: 'center', gap: '0px', flexDirection: 'column'}}>
            <p style={{fontSize: '12px', color: 'var(--blue)', textAlign: 'left'}}>Send mail to:</p>
         <p style={{fontSize: '16px', fontWeight: 'bold'}}> {rowData.business_name || rowData.lead_name || 'Unknown Contact'}</p>

          </div>
        </div>
        </div>
        {rowData.lead_type && (
          <div className="emailAiPopupBusinessType">
            {rowData.lead_type}
          </div>
        )}
      </div>
      
      <div className="emailAiPopupContent">
        {/* LINKE SPALTE: Topics, Style, Context */}
        <div className="emailAiPopupLeftColumn">
          <div className="emailAiPopupTopicSection">
            <h3 className="emailAiPopupSectionTitle">Topic</h3>
            <div className="emailAiPopupTopicButtons">
              {["Intro", "1. Follow Up", "2. Follow Up", "3. Follow Up"].map(topic => {
                // Mapping für die neuen Topic-Namen zu den internen Werten
                const originalTopicNames = {
                  "Intro": "Introduce Yourself",
                  "1. Follow Up": "Follow Up",
                  "2. Follow Up": "Second Follow Up",
                  "3. Follow Up": "Third Follow Up"
                };
                const originalTopic = originalTopicNames[topic];
                const topicType = topicTypeMapping[originalTopic];
                
                // Bestimme, ob das Topic basierend auf dem Kontaktstatus deaktiviert werden sollte
                let isDisabled = false;
                if (topic === "1. Follow Up" && rowData.lead_contacted !== "Introduced") {
                  isDisabled = rowData.lead_contacted !== "Introduced" && rowData.lead_contacted !== null;
                } else if (topic === "2. Follow Up" && rowData.lead_contacted !== "First Follow-up") {
                  isDisabled = rowData.lead_contacted !== "First Follow-up";
                } else if (topic === "3. Follow Up" && rowData.lead_contacted !== "Second Follow-up") {
                  isDisabled = rowData.lead_contacted !== "Second Follow-up";
                }
                
                // Mappe die neuen Topic-Namen zurück zu den originalen für den State-Vergleich
                const isActive = selectedTopic === originalTopic;
                
                return (
                  <button
                    key={topic}
                    className={`emailAiPopupTopicButton ${isActive ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                    onClick={() => !isDisabled && setSelectedTopic(originalTopic)}
                    disabled={isDisabled}
                  >
                    {topic}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="emailAiPopupStyleSection">
            <h3 className="emailAiPopupSectionTitle">Style</h3>
            <div className="emailAiPopupStyleButtons">
              <button
                onClick={() => setSelectedStyle("Professional & Supportive")}
                className={selectedStyle === "Professional & Supportive" ? "activeStyle" : ""}
              >
                <strong>Professional</strong>
                <span className="styleDescription">Formal, präzise, geschäftlich</span>
              </button>
              <button
                onClick={() => setSelectedStyle("Friendly & Authentic")}
                className={selectedStyle === "Friendly & Authentic" ? "activeStyle" : ""}
              >
                <strong>Casual</strong>
                <span className="styleDescription">Locker, warm, persönlich</span>
              </button>
            </div>
          </div>

          <div className="emailAiPopupContextSection">
            <h3 className="emailAiPopupSectionTitle">Description</h3>
            <div className="emailAiPopupContextWrapper">
              <textarea
                className="emailAiPopupContextInput"
                placeholder="Füge Details über den Empfänger hinzu..."
                value={contextInfo}
                onChange={(e) => setContextInfo(e.target.value)}
              ></textarea>
              
              {/* Social Media Buttons */}
              {rowData && (
                <div className="bonusclub-social-buttons-container">
                  {rowData.lead_website && (
                    <a 
                      href={formatURL(rowData.lead_website, 'https://')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-website"
                      title="Website besuchen"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="2" y1="12" x2="22" y2="12"></line>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                      </svg>
                    </a>
                  )}
                  {rowData.lead_instagram && (
                    <a 
                      href={formatURL(rowData.lead_instagram, 'https://instagram.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-instagram"
                      title="Instagram Profil"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                      </svg>
                    </a>
                  )}
                  {rowData.lead_facebook && (
                    <a 
                      href={formatURL(rowData.lead_facebook, 'https://facebook.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-facebook"
                      title="Facebook Seite"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                      </svg>
                    </a>
                  )}
                  {rowData.lead_linkedin && (
                    <a 
                      href={formatURL(rowData.lead_linkedin, 'https://linkedin.com/in/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-linkedin"
                      title="LinkedIn Profil"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                        <rect x="2" y="9" width="4" height="12"></rect>
                        <circle cx="4" cy="4" r="2"></circle>
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RECHTE SPALTE: Subject, Body, Extra Buttons, Generate/Send */}
        <div className="emailAiPopupRight">
          {/* Absender-E-Mail-Auswahl */}
          <div className="emailAiPopupField">
            <label><p className="emailAiPopupLabel">From:</p></label>
            <div className="emailAiPopupSenderEmailsContainer">
              {senderEmails.length > 0 ? (
                <div className="emailAiPopupSenderEmailsScroll">
                  {senderEmails.map((item) => (
                    <button 
                      key={item.email}
                      className={`emailAiPopupSenderEmailButton ${selectedSenderEmail === item.email ? 'active' : ''}`}
                      onClick={() => setSelectedSenderEmail(item.email)}
                    >
                      {item.sender_name 
                        ? `${item.sender_name} <${item.email}>` 
                        : item.email}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="emailAiPopupNoEmails">No sender emails configured.</p>
              )}
            </div>
          </div>

          {/* Empfänger-E-Mail-Auswahl */}
          <div className="emailAiPopupRecipientSection">
            <h3 className="emailAiPopupSectionTitle">To:</h3>
            <div className="emailAiPopupTopButtons">
              {emailAddresses.map((email, index) => (
                <div key={index} className="emailAiPopupButtonWrapper">
                  <button
                    className={`emailAiPopupButton ${email === selectedEmail ? 'active' : ''}`}
                    onClick={() => {
                      if (ccEmails.includes(email)) {
                        setCcEmails(ccEmails.filter(e => e !== email));
                      }
                      setSelectedEmail(email);
                    }}
                  >
                    {email}
                  </button>
                  
                  {email !== selectedEmail && (
                    <button 
                      className={`emailAiPopupCcButton ${ccEmails.includes(email) ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCcEmail(email);
                      }}
                      title={ccEmails.includes(email) ? "Remove from CC" : "Add to CC"}
                    >
                      {ccEmails.includes(email) ? "CC ✓" : "CC"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Betreff und Inhalt */}
          <input
            className="emailAiPopupSubjectInput"
            placeholder="Subject line"
            value={subjectLine}
            onChange={(e) => setSubjectLine(e.target.value)}
            ref={subjectInputRef}
          />

      
          <div className={`emailAiPopupTexfieldwrapper ${isGenerating ? 'loading' : ''}`} style={{position: 'relative'}}>
            <div className="salesgod-loading-container">
              <img src="/img/salesgod_dark.webp" alt="SalesGod" className="salesgod-loading-logo" style={{width: '40px', height: '40px'}} />
              <span style={{fontSize: '14px', fontWeight: 'bold', color: 'var(--blue)'}}>SalesGod Generating...</span>
            </div>
            <div className="emailAiPopupFormatButtons">
              <button 
                type="button" 
                onClick={() => {
                  const textarea = document.getElementById('emailContent');
                  const start = textarea.selectionStart;
                  const end = textarea.selectionEnd;
                  const selectedText = bodyContent.substring(start, end);
                  const newText = bodyContent.substring(0, start) + 
                                 `<b>${selectedText}</b>` + 
                                 bodyContent.substring(end);
                  setBodyContent(newText);
                }}
                title="Fett"
              >
                B
              </button>
              <button 
                type="button" 
                onClick={() => {
                  const textarea = document.getElementById('emailContent');
                  const start = textarea.selectionStart;
                  const end = textarea.selectionEnd;
                  const selectedText = bodyContent.substring(start, end);
                  const newText = bodyContent.substring(0, start) + 
                                 `<i>${selectedText}</i>` + 
                                 bodyContent.substring(end);
                  setBodyContent(newText);
                }}
                title="Kursiv"
              >
                I
              </button>
            </div>
            <textarea
              id="emailContent"
              className="emailAiPopupTextArea"
              value={bodyContent}
              onChange={(e) => setBodyContent(e.target.value)}
              placeholder="Schreibe deine E-Mail hier..."
            ></textarea>
          </div>
          <div className="emailAiPopupBottomButtons">
            <button 
              className={`emailAiPopupGenerateButton ${isGenerating ? 'loading' : ''}`} 
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <span className="emailAiPopupSpinner"></span>
              ) : (
                <img src="/img/star.svg" alt="AI" className="emailAiPopupGenerateIcon" />
              )}
            </button>
            <button
              className={`emailAiPopupSendButton ${sendSuccess ? 'success' : ''} ${isSending ? 'sending' : ''} ${!isEmailValid() ? 'disabled' : ''}`}
              onClick={handleSend}
              disabled={isGenerating || isSending || !isEmailValid()}
            >
              {isSending ? (
                <>
                  <div className="emailAiPopupSpinner"></div>
                  <span>Sending...</span>
                </>
              ) : sendSuccess ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Sent</span>
                </>
              ) : (
                <>
                  <span>Send Email</span>
                  <img src="/img/sender.svg" alt="Send" className="emailAiPopupSendIcon" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NormalModeEmail; 